/* © 2026 Wannlabs. Shared lazy-route helper: React.lazy + chunk-error retry.
 * After a deploy, old tabs ask for hashed chunks that no longer exist ("Failed to fetch dynamically imported module",
 * "ChunkLoadError", "Importing a module script failed"). We retry with backoff, then reload the page ONCE per
 * session (guarded in sessionStorage) so the user gets the new build instead of a white screen. */
import { lazy, type ComponentType, type LazyExoticComponent } from "react";

const CHUNK_RE = /dynamically imported module|ChunkLoadError|Loading chunk [\w-]+ failed|Importing a module script failed|error loading dynamically imported module|Failed to fetch/i;
export const RELOAD_KEY = "wl-chunk-reload";

export function isChunkError(err: unknown): boolean {
  const e = err as { name?: string; message?: string } | null;
  return !!e && (e.name === "ChunkLoadError" || CHUNK_RE.test(String(e.message ?? e)));
}

export interface RetryOptions { retries?: number; baseDelayMs?: number; reloadOnFail?: boolean; sleep?: (ms: number) => Promise<void>; reload?: () => void }

/** Run a dynamic import with retries; on a persistent chunk error, reload once per session (new deploy). */
export async function importWithRetry<T>(load: () => Promise<T>, opts: RetryOptions = {}): Promise<T> {
  const { retries = 2, baseDelayMs = 400, reloadOnFail = true } = opts;
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  let last: unknown;
  for (let i = 0; i <= retries; i++) {
    try {
      const mod = await load();
      try { sessionStorage.removeItem(RELOAD_KEY); } catch { /* private mode */ }
      return mod;
    } catch (err) {
      last = err;
      if (!isChunkError(err)) throw err;
      if (i < retries) await sleep(baseDelayMs * 2 ** i);
    }
  }
  if (reloadOnFail && typeof window !== "undefined") {
    let already = false;
    try { already = sessionStorage.getItem(RELOAD_KEY) === "1"; sessionStorage.setItem(RELOAD_KEY, "1"); } catch { /* ignore */ }
    if (!already) { (opts.reload ?? (() => window.location.reload()))(); return new Promise<T>(() => undefined); }
  }
  throw last;
}

/** React.lazy with chunk-error retry. `pick` selects a named export: lazyRoute(() => import("./X"), "XPage"). */
export function lazyRoute<M extends Record<string, unknown>, K extends keyof M>(
  load: () => Promise<M>,
  pick?: K,
  opts?: RetryOptions,
): LazyExoticComponent<ComponentType<any>> {
  return lazy(async () => {
    const mod = await importWithRetry(load, opts);
    const C = (pick ? mod[pick] : (mod as { default?: unknown }).default) as ComponentType<any>;
    return { default: C };
  });
}

/** Warm a route's chunk (on hover/idle) so the next navigation is instant. */
export function preload(load: () => Promise<unknown>): void { void importWithRetry(load, { reloadOnFail: false }).catch(() => undefined); }
